import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import ContactForm from './components/ContactForm';
import SalesTeamInfo from './components/SalesTeamInfo';
import TrustSignals from './components/TrustSignals';
import SuccessModal from './components/SuccessModal';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const ContactSales = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  const [currentYear] = useState(new Date()?.getFullYear());

  useEffect(() => {
    document.title = 'Contact Sales - MessagingHub Pro';
    window.scrollTo(0, 0);
  }, []);

  const handleFormSubmit = async (formData) => {
    setIsSubmitting(true);
    
    // Simulate API call
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      setSubmittedData(formData);
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
    setSubmittedData(null);
  };

  const breadcrumbs = [
    { label: 'Home', path: '/landing-page' },
    { label: 'Contact Sales', path: '/contact-sales', current: true }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      {/* Hero Section */}
      <section className="pt-24 pb-12 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Breadcrumbs */}
          <nav className="flex items-center space-x-2 text-sm mb-8">
            {breadcrumbs?.map((crumb, index) => (
              <React.Fragment key={crumb?.path}>
                {index > 0 && (
                  <Icon name="ChevronRight" size={14} className="text-muted-foreground" />
                )}
                <a
                  href={crumb?.path}
                  className={`transition-colors ${
                    crumb?.current
                      ? 'text-foreground font-medium'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {crumb?.label}
                </a>
              </React.Fragment>
            ))}
          </nav>

          {/* Hero Content */}
          <div className="text-center mb-12">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <Icon name="MessageSquare" size={32} className="text-primary" />
              </div>
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-4">
              Let's Build Something
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-secondary ml-3">
                Amazing Together
              </span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Connect with our sales team to discuss your messaging infrastructure needs. 
              Get personalized solutions, technical guidance, and enterprise support.
            </p>
            
            {/* Quick Stats */}
            <div className="flex flex-wrap justify-center gap-8 text-sm">
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={16} className="text-accent" />
                <span className="text-muted-foreground">2-hour response time</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Users" size={16} className="text-primary" />
                <span className="text-muted-foreground">500+ enterprise customers</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Shield" size={16} className="text-secondary" />
                <span className="text-muted-foreground">SOC 2 certified</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Main Content */}
      <section className="pb-16 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Form - Main Column */}
            <div className="lg:col-span-2">
              <ContactForm 
                onSubmit={handleFormSubmit} 
                isSubmitting={isSubmitting} 
              />
            </div>

            {/* Sales Team Info - Sidebar */}
            <div className="lg:col-span-1">
              <SalesTeamInfo />
            </div>
          </div>
        </div>
      </section>
      {/* Trust Signals Section */}
      <section className="py-16 px-6 lg:px-8 bg-muted/20">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Trusted by Industry Leaders
            </h2>
            <p className="text-muted-foreground">
              Join hundreds of companies that rely on MessagingHub Pro for their communication infrastructure
            </p>
          </div>
          <TrustSignals />
        </div>
      </section>
      {/* Alternative Contact Methods */}
      <section className="py-16 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-foreground mb-4">
              Other Ways to Get in Touch
            </h2>
            <p className="text-muted-foreground">
              Choose the method that works best for you
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Phone Support */}
            <div className="bg-card rounded-xl border border-border p-6 text-center shadow-elevation-1 hover:shadow-elevation-2 transition-shadow">
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                  <Icon name="Phone" size={24} className="text-primary" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Phone Support</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Speak directly with our sales team
              </p>
              <p className="text-lg font-mono text-foreground mb-4">+1 (800) MSG-HUBS</p>
              <p className="text-xs text-muted-foreground mb-4">
                Available 9 AM - 6 PM PST, Monday - Friday
              </p>
              <Button variant="outline" size="sm" iconName="Phone" iconPosition="left">
                Call Now
              </Button>
            </div>

            {/* Live Chat */}
            <div className="bg-card rounded-xl border border-border p-6 text-center shadow-elevation-1 hover:shadow-elevation-2 transition-shadow">
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center">
                  <Icon name="MessageCircle" size={24} className="text-accent" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Live Chat</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Get instant answers to your questions
              </p>
              <div className="flex items-center justify-center space-x-2 mb-4">
                <div className="w-2 h-2 bg-accent rounded-full"></div>
                <span className="text-sm text-accent">Online now</span>
              </div>
              <p className="text-xs text-muted-foreground mb-4">
                Average response time: &lt; 2 minutes
              </p>
              <Button variant="outline" size="sm" iconName="MessageCircle" iconPosition="left">
                Start Chat
              </Button>
            </div>

            {/* Email Support */}
            <div className="bg-card rounded-xl border border-border p-6 text-center shadow-elevation-1 hover:shadow-elevation-2 transition-shadow">
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center">
                  <Icon name="Mail" size={24} className="text-secondary" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Email Sales</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Send us detailed questions or requirements
              </p>
              <p className="text-sm font-mono text-foreground mb-4">sales@messaginghub.pro</p>
              <p className="text-xs text-muted-foreground mb-4">
                Response within 2 business hours
              </p>
              <Button variant="outline" size="sm" iconName="Mail" iconPosition="left">
                Send Email
              </Button>
            </div>
          </div>
        </div>
      </section>
      {/* Footer */}
      <footer className="bg-card border-t border-border py-12 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="md:col-span-1">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex items-center justify-center w-8 h-8 bg-primary rounded-lg">
                  <Icon name="MessageCircle" size={20} color="white" />
                </div>
                <span className="text-lg font-semibold text-foreground">MessagingHub Pro</span>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Enterprise messaging infrastructure for modern applications.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-muted-foreground hover:text-foreground transition-colors">
                  <Icon name="Twitter" size={20} />
                </a>
                <a href="#" className="text-muted-foreground hover:text-foreground transition-colors">
                  <Icon name="Linkedin" size={20} />
                </a>
                <a href="#" className="text-muted-foreground hover:text-foreground transition-colors">
                  <Icon name="Github" size={20} />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-4">Product</h4>
              <ul className="space-y-2">
                <li><a href="/api-documentation" className="text-sm text-muted-foreground hover:text-foreground transition-colors">API Documentation</a></li>
                <li><a href="/pricing-plans" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Pricing</a></li>
                <li><a href="/developer-portal" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Developer Portal</a></li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-4">Support</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Help Center</a></li>
                <li><a href="#" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Status Page</a></li>
                <li><a href="/contact-sales" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Contact Sales</a></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Terms of Service</a></li>
                <li><a href="#" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Security</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-border mt-8 pt-8 text-center">
            <p className="text-sm text-muted-foreground">
              © {currentYear} MessagingHub Pro. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={handleCloseModal}
        submittedData={submittedData}
      />
    </div>
  );
};

export default ContactSales;